import React, { useState, useContext, useEffect, useRef } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import BoxPlot, { getMetrics } from "common/BoxPlot";
import { getQuantileStats } from "common/api";

const GAP = 5;

export const BoxPlotWrapper = styled.div`
  g.extra-info-axis {
  display: none;
  }
  g.axis{
  display: none;
  }
  g.warning{
  transform: translate(40px, 0px) scale(0.35, 1);
  }
  rect.background {
  display: none;
  }
`;

const WrapperPlot = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  svg {
    text {
      font-size: 9px;
      fill: var(--Text-Secondary);
      background: red;
    }

    line {
      stroke: var(--Signal-Grey);
    }
  }
`;

const PlotAndExplanation = styled.div`
  margin: 0;
  display: flex;

  .demo1{
    position: relative;
    left: -50px;
  }
  .demo2{
    position: relative;
    left: -100px;
  }

  @media screen and (max-width: 670px) {
    height: 400px;
    .demo2{
      left: -400px;
      top: 200px;
      }
    }
  }
`;

const BoxPlotWarning = styled.div`
  width: 180px;
  height: 200px;
  position: absolute;
  left: 190px;
`;

const Label = styled.div`
  position: absolute;
  font-size: 12px;
  &.label1 {
    top: 30px;
    left: 20px;
  }
  &.label2 {
    top: 52px;
    left: 20px;
  }
  &.label3 {
    color: #cc0000;
    font-size: 50px;
  }
  &.label4 {
    left: 20px;
    top: 28px;
  }
  &.label5 {
    left: 20px;
    top: 83px;
  }
  &.label6 {
    left: 20px;
    top: 110px;
  }
  &.label7 {
    left: 20px;
    top: 130px;
  }
  &.label9 {
    color: #cc0000;
    font-size: 60px;
    top: -2px;
    font-weight: lighter;
  }
  &.label10 {
    color: #e69138;
    font-size: 29px;
    top: 69px;
    font-weight: bold;
  }
  &.label11 {
    color: #6aa84f;
    font-size: 26px;
    top: 99px;
    font-weight: bold;
  }
  &.label12 {
    color: #3d85c6;
    font-size: 55px;
    top: 120px;
    font-weight: lighter;
  }
`;

const BoxPlotDescriptionQuantiles = styled.div`
  width: 170px;
  height: 200px;
  position: absolute;
  top: 0px;
  left: 160px;
`;

const MARGIN = { top: 10, right: 10, bottom: 10, left: 20 };

const BoxPlotDescription = ({
  quantileData,
  width,
  height,
  margin,
  chartMin,
  chartMax,
}) => {
  const boundsWidth = width - margin.right - margin.left;
  const boundsHeight = height - margin.top - margin.bottom;
  const [plot, setPlot] = useState({ x: 0, y: 0 });
  const plotRef = useRef(null);

  useEffect(() => {
    if (plotRef.current) {
      const rect = plotRef.current.getBoundingClientRect();
      setPlot({ x: rect.left, y: rect.top });
    }
  }, [width, height]);

  const yScale = d3
    .scaleLinear()
    .domain([chartMin, chartMax])
    .range([boundsHeight - GAP, GAP]);

  const { min, q1, median, q3, max, p10, p90 } = quantileData;

  return (
    <WrapperPlot ref={plotRef}>
      <svg width={width} height={height}>
        <g
          width={boundsWidth}
          height={boundsHeight}
          transform={`translate(${[margin.left, -13].join(",")})`}
        >
          {getMetrics(quantileData).map(({ key, label, value }, index) => {
            if (value === undefined) return null;
            return (
              <React.Fragment key={key}>
                <text
                  x={-10}
                  y={(height / 7) * (index + 1) + 3}
                  textAnchor="begin"
                >
                  {label}
                </text>
                <line
                  x1={20}
                  x2={width}
                  y1={(height / 7) * (index + 1)}
                  y2={(height / 7) * (index + 1)}
                  stroke-dasharray="0 3 0"
                />
              </React.Fragment>
            );
          })}
        </g>
      </svg>
    </WrapperPlot>
  );
};

const BoxPlotDemo = ({
  data = ["1000", "700", "600", "500", "400", "250", "10"],
  inverted = true,
  colorizeQuartiles = false,
  units = undefined,
  format = undefined,
  yGap = 0.1 /*10%*/,
}) => {
  const quantileData = getQuantileStats(data);
  const [chartMin, chartMax] = d3.extent(data);
  return (
    <PlotAndExplanation>
      <BoxPlotDescription
        quantileData={quantileData}
        width={60}
        height={200}
        margin={MARGIN}
        chartMin={chartMin}
        chartMax={chartMax}
      />
      <BoxPlotWrapper className="demo1">
        <BoxPlot
          data={data}
          width={300}
          height={200}
          margin={MARGIN}
          units={units}
          colorize={false}
          inverted={false}
          threshold={750}
        />
      </BoxPlotWrapper>
      <BoxPlotWarning>
        <Label className="label1">Values over threshold</Label>
        <Label className="label2">Warning threshold</Label>
        <Label className="label3">}</Label>
      </BoxPlotWarning>
      <BoxPlotWrapper className="demo2">
        <BoxPlot
          data={data}
          width={300}
          height={200}
          margin={MARGIN}
          units={units}
          colorize={true}
          inverted={true}
        />
        <BoxPlotDescriptionQuantiles>
          <Label className="label4">
            4th quartile
            <br />
            (highest 25% of values)
          </Label>
          <Label className="label5">3rd quartile</Label>
          <Label className="label6">2nd quartile</Label>
          <Label className="label7">
            1st quartile
            <br />
            (lowest 25% of values)
          </Label>
          <Label className="label9">}</Label>
          <Label className="label10">}</Label>
          <Label className="label11">}</Label>
          <Label className="label12">}</Label>
        </BoxPlotDescriptionQuantiles>
      </BoxPlotWrapper>
    </PlotAndExplanation>
  );
};

export default BoxPlotDemo;